import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import {
  createLegalTranslationProsecutorAssignment,
  createLegalTranslationRemark,
  deleteLegalTranslationProsecutorAssignment,
  updateLegalTranslation,
  updateLegalTranslationProsecutorAssignment,
} from "services/legalTranslationService";
import { LoadingIcon } from "utils/constant";
import { LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  isTeamLeader: boolean;
  prosecutor_name?: string;
  is_team_leader?: boolean;
}

const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
};

export default function CoordinatorRemark({
  setEdit,
  setFormState,
  formState,
  type,
  setupTeam: externalSetupTeam,
  setSetupTeam: externalSetSetupTeam,
}: {
  setEdit: any;
  setFormState: any;
  formState: any;
  type?: any;
  setupTeam?: boolean;
  setSetupTeam?: any;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [selectedProsecutor, setSelectedProsecutor] =
    useState<TeamMember | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(
    (formState.prosecutor_assignments ?? []).map((member: any) => ({
      ...member,
      isTeamLeader: member.isTeamLeader ?? member.is_team_leader ?? member.role === "leader" ? true : false,
      id: member.prosecutor_user_id,
      name: member.prosecutor_name,
      role: "Prosecutor",
    }))
  );
  const [employees, setEmployees] = useState([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [internalSetupTeam, setInternalSetupTeam] = useState<boolean>(true);
  const setupTeamInternal = externalSetupTeam !== undefined ? externalSetupTeam : internalSetupTeam;
  const setSetupTeamInternal = externalSetSetupTeam || setInternalSetupTeam;
  const contextData = useAuth();

  useEffect(() => {
    getEmployeesByAdminAndOrg({
      bearer:
        contextData.token ??
        contextData?.user?.access_token ??
        contextData?.user?.user?.access_token,
      org_unit: contextData?.user?.org_unit || "",
      admin_unit: contextData?.user?.admin_unit || "",
    })
      .then((data) => {
        setEmployees(
          data
            .filter((emp: any) =>
              emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
            )
            .map((emp: any) => ({
              id: emp.user.id,
              name: emp.user.first_name,
              role: emp.roles[0] || "LegalResearchProsecutor",
              isTeamLeader: false,
            }))
        );
      })
      .catch((error) => {
        console.error("Error fetching:", error);
        showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
      });
  }, [contextData, showToast, t]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveRemark = async () => {
    const remarkData = new FormData();
    remarkData.append("request", formState.id);
    remarkData.append("role", "coordinator");
    remarkData.append("remark", formState.remark);
    await createLegalTranslationRemark(remarkData);
  };

  const saveTeam = async () => {
    const currentIds = teamMembers.map((m) => m.id.toString());

    for (const prosecutor of (formState.prosecutor_assignments ?? [])) {
      if (!currentIds.includes(prosecutor.prosecutor_user_id)) {
        await deleteLegalTranslationProsecutorAssignment(prosecutor.id);
      }
    }

    for (const member of teamMembers) {
      const prosecutorData = new FormData();
      prosecutorData.append("request", formState.id);
      prosecutorData.append("prosecutor_name", member.name);
      prosecutorData.append("prosecutor_user_id", member.id.toString());
      prosecutorData.append("role", member.isTeamLeader ? "leader" : "member");
      prosecutorData.append("is_team_leader", member.isTeamLeader ? "true" : "false");

      const existing = (formState.prosecutor_assignments ?? []).find(
        (p: any) => p.prosecutor_user_id === member.id.toString()
      );
      if (existing) {
        await updateLegalTranslationProsecutorAssignment(existing.id, prosecutorData);
      } else {
        await createLegalTranslationProsecutorAssignment(prosecutorData);
      }
    }
  };

  const handleForwardToCoordinator = async () => {
    if (!formState.remark?.trim()) {
      showToast(t("PleaseWriteRemarkBeforeForwarding") || "Please write a remark before forwarding.", "error");
      return;
    }
    setLoading(true);
    try {
      await saveRemark();
      const data = new FormData();
      data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.coordinator_remark_submitted.value);
      data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value);
      const response = await updateLegalTranslation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("ForwardedToCoordinatorSuccess") || "Forwarded to Coordinator successfully!", "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToForwardRequest") || "Failed to forward request.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForwardToProsecutor = async () => {
    if (!formState.remark?.trim()) {
      showToast(t("PleaseWriteRemarkBeforeForwarding") || "Please write a remark before forwarding.", "error");
      return;
    }
    if (teamMembers.length === 0) {
      showToast(t("PleaseAddTeamMemberBeforeForwarding") || "Please setup at least one team member.", "error");
      return;
    }
    setLoading(true);
    try {
      await saveRemark();
      await saveTeam();
      const data = new FormData();
      data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.coordinator_remark_submitted.value);
      data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value);
      const response = await updateLegalTranslation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("TeamSetupAndForwardedSuccess") || "Team setup and forwarded to Prosecutor successfully!", "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToForwardRequest") || "Failed to forward request.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOnly = async () => {
    setLoading(true);
    try {
      await saveRemark();
      const data = new FormData();
      data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.coordinator_remark_submitted.value);
      const response = await updateLegalTranslation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("RemarkSavedSuccess") || "Remark saved successfully!", "success");
      setEdit(false);
    } catch (error) {
      console.error("Error saving remark:", error);
      showToast(t("FailedToSaveRemark") || "Failed to save remark.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = (id: number) => {
    const memberToRemove = teamMembers.find((member) => member.id === id);
    if (
      memberToRemove?.isTeamLeader &&
      teamMembers.filter((m) => m.isTeamLeader).length === 1
    ) {
      setValidationError(
        t("CannotRemoveOnlyTeamLeader") || "Cannot remove the only team leader. Please assign a new team leader first or add another member."
      );
      return;
    }
    setTeamMembers(teamMembers.filter((member) => member.id !== id));
    setValidationError(null);
  };

  const handleToggleTeamLeader = (id: number) => {
    setTeamMembers((prevMembers) =>
      prevMembers.map((member) => {
        if (member.id === id) {
          return { ...member, isTeamLeader: !member.isTeamLeader };
        } else {
          return { ...member, isTeamLeader: false };
        }
      })
    );
    setValidationError(null);
  };

  const handleAddProsecutor = () => {
    if (
      selectedProsecutor &&
      !teamMembers.some((member) => member.id === selectedProsecutor.id)
    ) {
      setTeamMembers((prevMembers) => [
        ...prevMembers,
        {
          id: selectedProsecutor.id,
          name: selectedProsecutor.name,
          role: "Prosecutor",
          isTeamLeader: prevMembers.length === 0,
        },
      ]);
      setSelectedProsecutor(null);
      setValidationError(null);
    } else if (
      teamMembers.some((member) => member.id === selectedProsecutor?.id)
    ) {
      showToast(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.", "error");
    }
  };

  return (
    <>
      <div className="mt-6">
        <label
          htmlFor="remark"
          className="block text-sm font-medium text-gray-500"
        >
          {t("Remark") || "Remark"}
        </label>
        <textarea
          id="remark"
          name="remark"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
          placeholder={t("DetailHere") || "Detail here...."}
          required
          value={formState.remark}
          onChange={handleChange}
        ></textarea>

        <div className="mb-6 mt-4">
          <label
            htmlFor="select-prosecutor"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t("SetupTeamMembersOptional") || "Setup Team Members (Optional)"}
          </label>
          <div className="relative mb-3">
            <select
              id="select-prosecutor"
              className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-white pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
              value={selectedProsecutor?.id || ""}
              onChange={(e) =>
                setSelectedProsecutor(
                  employees.find(
                    (employee: any) => String(employee.id) === e.target.value
                  ) ?? null
                )
              }
            >
              <option value="">{t("SelectProsecutor") || "Select Prosecutor"}</option>
              {employees.map((emp: any) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} - {emp.role}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          <button
            type="button"
            onClick={handleAddProsecutor}
            className="mb-4 text-[#AA232F] hover:text-blue-800 text-sm"
          >
            {t("AddSelectedProsecutorToTeam") || "Add Selected Prosecutor to Team"}
          </button>

          <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
            {teamMembers.length === 0 && (
              <p className="text-gray-500 text-sm italic">
                {t("NoTeamMembersAddedYetOptional") || "No team members added yet. Optional if forwarding to Coordinator."}
              </p>
            )}
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
              >
                <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                  {getInitials(member.name ?? member.prosecutor_name)}
                </div>

                <div className="flex-grow">
                  <p className="font-semibold text-gray-800">
                    {member.name ?? member.prosecutor_name}
                  </p>
                  <p className="text-sm text-gray-500">{member.role ?? "Prosecutor"}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`team-leader-${member.id}`}
                    checked={member.isTeamLeader ?? member.role === "leader"}
                    onChange={() => handleToggleTeamLeader(member.id)}
                    className="accent-[#AA232F] form-checkbox h-4 w-4 text-gray-800 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`team-leader-${member.id}`}
                    className="text-sm text-gray-700"
                  >
                    {t("IsTeamLeader") || "is Team Leader"}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#AA232F"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {validationError && (
            <p className="text-red-500 text-sm mt-2">{validationError}</p>
          )}
        </div>
      </div>
      <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4">
        <button
          type="button"
          className="flex-1 px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600 disabled:opacity-50"
          onClick={setupTeamInternal ? handleForwardToProsecutor : handleSaveOnly}
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon} {t("Submitting") || "Submitting..."}
            </div>
          ) : (
            setupTeamInternal ? (t("SetupTeamAndForwardToProsecutor") || "Setup Team & Forward to Prosecutor") : (t("SaveOnly") || "Save Only")
          )}
        </button>
      </div>
    </>
  );
}